import { useEffect, useMemo, useState } from 'react';
import { Heading, Flex, useToasts } from '@applyboard/crystal-ui';

import { useUpdateConversionTableRules } from '../data/useUpdateConversionTableRules';
import { useGradeConversionRulesEditor } from '~/hooks/useGradeConversionRulesEditor';
import { ConversionTableMetadataForm } from '../../../components/ConversionTableMetadataForm/ConversionTableMetadataForm';
import { NumericDestinationRulesTable } from './NumericDestinationRulesTable';
import { LetterDestinationRulesTable } from './LetterDestinationRulesTable';
import { RulesTableActions } from './RulesTableActions';
import { GradeSchemaInfo, GradePointMapping } from '~/types/gradeConversion';
import { GradeConversion } from '../data/useGradeConversion';
import { getApiErrorMessage } from '~/utils';
import { CountryOption, EducationLevelOption } from '~/pages/GradeConversion/utils/referenceData';

type MetadataState = {
  name: string;
  destinationGradeType: 'numeric' | 'letter';
  sourceInstitutionName: string;
  sourceCountryCode: string;
  gradeSchemaId: string;
  sourceEducationLevelId: string;
};

interface GradeConversionInformationProps {
  conversionTable: GradeConversion;
  gradeSchema: GradeSchemaInfo;
  gradePointMappings: GradePointMapping[];
  educationLevels: EducationLevelOption[];
  countries: CountryOption[];
}

export function GradeConversionInformation({
  conversionTable,
  gradeSchema,
  gradePointMappings,
  educationLevels,
  countries,
}: GradeConversionInformationProps) {
  const { updateConversionTableRules, isUpdating } = useUpdateConversionTableRules();
  const conversionTableId = conversionTable.id;
  const toast = useToasts();

  const { rules, isDirty, addRule, removeRule, updateRule, resetRules, validateAll, getRulesForSubmission } =
    useGradeConversionRulesEditor({
      initialRules: conversionTable.rules,
      gradeSchema,
      destinationGradeType: conversionTable.destinationGradeType,
      gradePointMappings,
    });

  const educationLevelResource = conversionTable.included?.find((resource) => resource.type === 'education_level') as
    | {
        id: string;
        type: 'education_level';
        attributes: { name: string; code: string };
      }
    | undefined;

  const selectedEducationLevelId = educationLevelResource?.id ?? '';

  const initialMetadata = useMemo<MetadataState>(
    () => ({
      name: conversionTable.name,
      destinationGradeType: conversionTable.destinationGradeType,
      sourceInstitutionName: conversionTable.sourceInstitutionName ?? '',
      sourceCountryCode: conversionTable.sourceCountry?.alpha2Code ?? '',
      gradeSchemaId: gradeSchema.id,
      sourceEducationLevelId: selectedEducationLevelId,
    }),
    [
      conversionTable.destinationGradeType,
      conversionTable.name,
      conversionTable.sourceCountry?.alpha2Code,
      conversionTable.sourceInstitutionName,
      gradeSchema.id,
      selectedEducationLevelId,
    ]
  );

  const [metadata, setMetadata] = useState<MetadataState>(initialMetadata);

  useEffect(() => {
    setMetadata(initialMetadata);
  }, [initialMetadata]);

  const handleMetadataChange = <K extends keyof MetadataState>(key: K, value: MetadataState[K]) => {
    setMetadata((prev) => ({ ...prev, [key]: value }));
  };

  const metadataDirty = useMemo(() => {
    return (
      metadata.name.trim() !== initialMetadata.name.trim() ||
      metadata.sourceInstitutionName.trim() !== initialMetadata.sourceInstitutionName.trim() ||
      metadata.sourceCountryCode !== initialMetadata.sourceCountryCode ||
      metadata.sourceEducationLevelId !== initialMetadata.sourceEducationLevelId
    );
  }, [initialMetadata, metadata]);

  const gradeSchemaOptions = [
    {
      id: gradeSchema.id,
      name: gradeSchema.name,
      gradeSchemaType: gradeSchema.gradeSchemaType,
      minGrade: gradeSchema.minGrade,
      maxGrade: gradeSchema.maxGrade,
      validGrades: gradeSchema.validGrades,
    },
  ];

  const selectedCountryCode = conversionTable.sourceCountry?.alpha2Code;
  const selectedCountryLabel = conversionTable.sourceCountry?.countryLabel;

  const countryOptions = useMemo(() => {
    if (!selectedCountryCode || !selectedCountryLabel) {
      return countries;
    }
    const hasSelected = countries.some((country) => country.code === selectedCountryCode);
    if (hasSelected) {
      return countries;
    }
    return [...countries, { code: selectedCountryCode, label: selectedCountryLabel }];
  }, [countries, selectedCountryCode, selectedCountryLabel]);

  const selectedEducationLevelName = educationLevelResource?.attributes?.name;

  const educationLevelOptions = useMemo(() => {
    if (!selectedEducationLevelId || !selectedEducationLevelName) {
      return educationLevels;
    }
    const hasSelected = educationLevels.some((level) => level.id === selectedEducationLevelId);
    if (hasSelected) {
      return educationLevels;
    }
    return [...educationLevels, { id: selectedEducationLevelId, name: selectedEducationLevelName }];
  }, [educationLevels, selectedEducationLevelId, selectedEducationLevelName]);

  const handleSave = async () => {
    try {
      if (!metadata.name.trim()) {
        toast.negative(new Error('Conversion table name is required.'));
        return;
      }

      const isValid = validateAll();
      if (!isValid) {
        return;
      }

      await updateConversionTableRules({
        id: conversionTableId,
        rules: getRulesForSubmission(),
        attributes: {
          name: metadata.name.trim(),
          sourceInstitutionName: metadata.sourceInstitutionName.trim() || null,
          sourceCountryCode: metadata.sourceCountryCode || null,
          sourceEducationLevel: metadata.sourceEducationLevelId || null,
        },
      });
      toast.positive('Grade conversion rules updated');
    } catch (error) {
      toast.negative(
        new Error(getApiErrorMessage(error, 'Failed to update grade conversion rules. Please try again.'), {
          cause: error,
        })
      );
    }
  };

  const handleCancel = () => {
    resetRules();
    setMetadata(initialMetadata);
  };

  return (
    <Flex direction="column" gap={6}>
      <ConversionTableMetadataForm
        value={metadata}
        countries={countryOptions}
        gradeSchemas={gradeSchemaOptions}
        educationLevels={educationLevelOptions}
        isGradeSchemasLoading={false}
        disabledFields={{
          gradeSchemaId: true,
          destinationGradeType: true,
        }}
        onChange={handleMetadataChange}
      />

      <Flex direction="column" gap={4}>
        <Heading contrast="high" level={3} variant="titleS">
          Conversion Rules
        </Heading>

        {conversionTable.destinationGradeType === 'numeric' ? (
          <NumericDestinationRulesTable
            rules={rules}
            gradeSchema={gradeSchema}
            onUpdateRule={updateRule}
            onRemoveRule={removeRule}
          />
        ) : (
          <LetterDestinationRulesTable
            rules={rules}
            gradeSchema={gradeSchema}
            gradePointMappings={gradePointMappings}
            onUpdateRule={updateRule}
            onRemoveRule={removeRule}
          />
        )}

        <RulesTableActions
          onAddRule={addRule}
          onSave={handleSave}
          onCancel={handleCancel}
          isDirty={isDirty || metadataDirty}
          isSaving={isUpdating}
        />
      </Flex>
    </Flex>
  );
}
